import posthog from "posthog-js";
import type { Exception } from "@posthog/core/error-tracking";
import { shouldSuppressChunkLoadException } from "@/util/chunkLoadRecovery";
import { logger } from "@/util/logger";

const IS_PROD = process.env.NEXT_PUBLIC_ENV === "prod";
const ROLEPLAY_REPLAY_IDLE_MS = 2 * 60 * 60 * 1000; // 2 hours

/**
 * Global Event Registry — App (User) Events
 * Funnels 3–8 covering Login, Trainings, Training Details,
 * Roleplay Pre-flight, Roleplay Session, and Roleplay Result.
 */
export enum GA_EVENTS {
  // ─── Funnel 3: App Login ──────────────────────────────────────────────────
  APP_LOGIN_PAGE_VIEWED = "app_login_page_viewed",
  APP_LOGIN_INITIATED = "login_initiated",
  APP_LOGIN_EMAIL_ENTERED = "app_login_email_entered",
  APP_LOGIN_PRIVACY_POLICY_CLICKED = "app_login_privacy_policy_clicked",
  APP_LOGIN_SUPPORT_EMAIL_CLICKED = "app_login_support_email_clicked",
  APP_LOGIN_PASSWORD_STEP_VIEWED = "app_login_password_step_viewed",
  APP_LOGIN_CLEAR_EMAIL_CLICKED = "app_login_clear_email_clicked",
  APP_LOGIN_PASSWORD_VISIBILITY_TOGGLED = "app_login_password_visibility_toggled",
  APP_FORGOT_PASSWORD_CLICKED = "app_forgot_password_clicked",
  APP_LOGIN_PASSWORD_ENTERED = "app_login_password_entered",
  APP_LOGIN_SUCCESS = "app_login_success",
  APP_LOGIN_FAILED = "app_login_failed",

  // ─── Funnel 4: Trainings List ─────────────────────────────────────────────
  APP_TRAININGS_PAGE_VIEWED = "app_trainings_page_viewed",
  APP_NOTIFICATIONS_CLICKED = "app_notifications_clicked",
  APP_SUPPORT_CLICKED = "app_support_clicked",
  APP_TRAINING_SEARCHED = "app_training_searched",
  APP_SEE_ALL_CLICKED = "app_see_all_clicked",
  APP_TRAINING_CARD_CLICKED = "app_training_card_clicked",
  APP_START_TRAINING_CLICKED = "app_start_training_clicked",
  APP_BOTTOM_NAV_CLICKED = "app_bottom_nav_clicked",

  // ─── Funnel 5: Training Details ───────────────────────────────────────────
  APP_TRAINING_DETAILS_VIEWED = "app_training_details_viewed",
  APP_TRAINING_READ_MORE_CLICKED = "app_training_read_more_clicked",
  APP_TRAINING_DETAILS_BACK_CLICKED = "app_training_details_back_clicked",
  APP_MODULE_START_CLICKED = "app_module_start_clicked",
  APP_MODULE_HISTORY_CLICKED = "app_module_history_clicked",
  APP_AI_ASSISTANT_CLICKED = "app_ai_assistant_clicked",

  // ─── Funnel 6: Roleplay Pre-flight ───────────────────────────────────────
  APP_ROLEPLAY_DESCRIPTION_VIEWED = "app_roleplay_description_viewed",
  APP_ROLEPLAY_BACK_CLICKED = "app_roleplay_back_clicked",
  APP_ROLEPLAY_HINT_TOGGLED = "app_roleplay_hint_toggled",
  APP_ROLEPLAY_EXIT_CLICKED = "app_roleplay_exit_clicked",
  APP_ROLEPLAY_NEXT_CLICKED = "app_roleplay_next_clicked",
  APP_ROLEPLAY_MIC_SETUP_VIEWED = "app_roleplay_mic_setup_viewed",
  APP_ROLEPLAY_LANGUAGE_SELECTED = "app_roleplay_language_selected",
  APP_ROLEPLAY_VIDEO_MODE_SELECTED = "app_roleplay_video_mode_selected",
  APP_ROLEPLAY_MIC_SELECTED = "app_roleplay_mic_selected",
  APP_ROLEPLAY_SPEAKER_SELECTED = "app_roleplay_speaker_selected",
  APP_ROLEPLAY_TEST_SPEAKER_CLICKED = "app_roleplay_test_speaker_clicked",
  APP_ROLEPLAY_PREVIOUS_CLICKED = "app_roleplay_previous_clicked",
  APP_ROLEPLAY_START_CLICKED = "app_roleplay_start_clicked",

  // ─── Funnel 7: Roleplay Session (In-call) ────────────────────────────────
  APP_ROLEPLAY_SESSION_STARTED = "app_roleplay_session_started",
  APP_ROLEPLAY_VIEW_SCENARIO_CLICKED = "app_roleplay_view_scenario_clicked",
  APP_ROLEPLAY_CAPTION_TAB_CLICKED = "app_roleplay_caption_tab_clicked",
  APP_ROLEPLAY_HINTS_TAB_CLICKED = "app_roleplay_hints_tab_clicked",
  APP_ROLEPLAY_INTERRUPT_CLICKED = "app_roleplay_interrupt_clicked",
  APP_ROLEPLAY_MUTE_TOGGLED = "app_roleplay_mute_toggled",
  APP_ROLEPLAY_RESTART_CLICKED = "app_roleplay_restart_clicked",
  APP_ROLEPLAY_CALL_ENDED = "app_roleplay_call_ended",
  /** Audio-session breadcrumbs — no DOM activity during streaming roleplays. */
  APP_ROLEPLAY_MIC_SWITCHED = "app_roleplay_mic_switched",
  APP_ROLEPLAY_SOCKET_DISCONNECTED = "app_roleplay_socket_disconnected",
  APP_ROLEPLAY_SOCKET_RECONNECTED = "app_roleplay_socket_reconnected",
  APP_ROLEPLAY_CONNECTION_LOST = "app_roleplay_connection_lost",
  ROLEPLAY_SLOW_CONNECTION_DETECTED = "roleplay_slow_connection_detected",
  APP_ROLEPLAY_CONNECTION_ERROR = "app_roleplay_connection_error",
  APP_ROLEPLAY_INACTIVITY_DISCONNECT = "app_roleplay_inactivity_disconnect",
  APP_ROLEPLAY_HEARING_ERROR = "app_roleplay_hearing_error",
  APP_ROLEPLAY_AVATAR_DISCONNECTED = "app_roleplay_avatar_disconnected",
  APP_ROLEPLAY_START_FAILED = "app_roleplay_start_failed",
  APP_ROLEPLAY_OFFLINE = "app_roleplay_offline",
  APP_ROLEPLAY_ONLINE = "app_roleplay_online",
  APP_ROLEPLAY_SESSION_ACTIVE = "app_roleplay_session_active",

  // ─── Funnel 8: Roleplay Result ────────────────────────────────────────────
  APP_ROLEPLAY_RESULT_IN_PROGRESS_VIEWED = "app_roleplay_result_in_progress_viewed",
  APP_ROLEPLAY_RESULT_REFRESH_CLICKED = "app_roleplay_result_refresh_clicked",
  APP_ROLEPLAY_RESULT_BACK_TO_TRAININGS_CLICKED = "app_roleplay_result_back_to_trainings_clicked",
  APP_ROLEPLAY_RESULT_VIEWED = "app_roleplay_result_viewed",
  APP_ROLEPLAY_RESULT_BACK_CLICKED = "app_roleplay_result_back_clicked",
  APP_ROLEPLAY_SESSION_DURATION_CLICKED = "app_roleplay_session_duration_clicked",
  APP_ROLEPLAY_RESULT_TAB_CLICKED = "app_roleplay_result_tab_clicked",
  APP_ROLEPLAY_TRY_WITHOUT_HINT_CLICKED = "app_roleplay_try_without_hint_clicked",
  APP_ROLEPLAY_PREVIOUS_MODULE_CLICKED = "app_roleplay_previous_module_clicked",
  APP_ROLEPLAY_NEXT_MODULE_CLICKED = "app_roleplay_next_module_clicked",

  // ─── Activity Module ──────────────────────────────────────────────────────
  APP_ACTIVITY_MODULE_START_CLICKED = "app_activity_module_start_clicked",
  APP_ACTIVITY_MODULE_HISTORY_CLICKED = "app_activity_module_history_clicked",
  APP_ACTIVITY_PREFLIGHT_VIEWED = "app_activity_preflight_viewed",
  APP_ACTIVITY_LANGUAGE_SELECTED = "app_activity_language_selected",
  APP_ACTIVITY_STARTED = "app_activity_started",
  APP_ACTIVITY_CANCELLED = "app_activity_cancelled",
  APP_ACTIVITY_REPORT_VIEWED = "app_activity_report_viewed",
  APP_ACTIVITY_BACK_TO_SECTIONS_CLICKED = "app_activity_back_to_sections_clicked",
  APP_PREVIOUS_MODULE_CLICKED = "app_previous_module_clicked",
  APP_NEXT_MODULE_CLICKED = "app_next_module_clicked",

  // ─── Global Bot ───────────────────────────────────────────────────────────
  GLOBAL_BOT_PAGE_VIEWED = "global_bot_page_viewed",

  // ─── Coach Module ─────────────────────────────────────────────────────────
  APP_COACH_MODULE_START_CLICKED = "app_coach_module_start_clicked",
  APP_COACH_MODULE_HISTORY_CLICKED = "app_coach_module_history_clicked",
  APP_COACH_DESCRIPTION_VIEWED = "app_coach_description_viewed",
  APP_COACH_EXIT_CLICKED = "app_coach_exit_clicked",
  APP_COACH_NEXT_CLICKED = "app_coach_next_clicked",
  APP_COACH_PREFLIGHT_VIEWED = "app_coach_preflight_viewed",
  APP_COACH_LANGUAGE_SELECTED = "app_coach_language_selected",
  APP_COACH_MODE_SELECTED = "app_coach_mode_selected",
  APP_COACH_PREVIOUS_CLICKED = "app_coach_previous_clicked",
  APP_COACH_STARTED = "app_coach_started",
  APP_COACH_MUTE_TOGGLED = "app_coach_mute_toggled",
  APP_COACH_RESTART_CLICKED = "app_coach_restart_clicked",
  APP_COACH_CALL_ENDED = "app_coach_call_ended",
  APP_COACH_RESULT_VIEWED = "app_coach_result_viewed",

  // ─── Assessment Module ────────────────────────────────────────────────────
  APP_ASSESSMENT_MODULE_START_CLICKED = "app_assessment_module_start_clicked",
  APP_ASSESSMENT_MODULE_HISTORY_CLICKED = "app_assessment_module_history_clicked",
  APP_ASSESSMENT_PREFLIGHT_VIEWED = "app_assessment_preflight_viewed",
  APP_ASSESSMENT_LANGUAGE_SELECTED = "app_assessment_language_selected",
  APP_ASSESSMENT_STARTED = "app_assessment_started",
  APP_ASSESSMENT_CANCELLED = "app_assessment_cancelled",
  APP_ASSESSMENT_SUBMITTED = "app_assessment_submitted",
  APP_ASSESSMENT_CANCELLED_IN_TEST = "app_assessment_cancelled_in_test",
  APP_ASSESSMENT_RESULT_VIEWED = "app_assessment_result_viewed",

  // ─── Evaluation Module ────────────────────────────────────────────────────
  APP_EVALUATION_MODULE_START_CLICKED = "app_evaluation_module_start_clicked",
  APP_EVALUATION_MODULE_HISTORY_CLICKED = "app_evaluation_module_history_clicked",
  APP_EVALUATION_PREFLIGHT_VIEWED = "app_evaluation_preflight_viewed",
  APP_EVALUATION_EXIT_CLICKED = "app_evaluation_exit_clicked",
  APP_EVALUATION_NEXT_CLICKED = "app_evaluation_next_clicked",
  APP_EVALUATION_CAMERA_SELECTED = "app_evaluation_camera_selected",
  APP_EVALUATION_MIC_SELECTED = "app_evaluation_mic_selected",
  APP_EVALUATION_SPEAKER_SELECTED = "app_evaluation_speaker_selected",
  APP_EVALUATION_TEST_SPEAKER_CLICKED = "app_evaluation_test_speaker_clicked",
  APP_EVALUATION_STARTED = "app_evaluation_started",
  APP_EVALUATION_RESULT_VIEWED = "app_evaluation_result_viewed",

  // ─── Funnel 1: Job Application ──────────────────────────────────────────
  JOB_LISTING_VIEWED = "job_listing_viewed",
  APPLY_NOW_CLICKED = "apply_now_clicked",
  RESUME_UPLOAD_VIEWED = "resume_upload_viewed",
  CANDIDATE_DETAILS_VIEWED = "candidate_details_viewed",
  CANDIDATE_DETAILS_SUBMITTED = "candidate_details_submitted",
  APPLICATION_SUBMITTED = "application_submitted",
  RESUME_UPLOAD_SKIPPED = "resume_upload_skipped",
  APPLY_ABANDONED = "apply_abandoned",
  RESUME_FILE_SELECTED = "resume_file_selected",
  DETAILS_FIELD_EDITED = "details_field_edited",
  RESUME_UPLOAD_RETRIED = "resume_upload_retried",

  // ─── Funnel 2: Pre-Interview Setup ───────────────────────────────────────
  START_INTERVIEW_CLICKED = "start_interview_clicked",
  INTERVIEW_INTRO_VIEWED = "interview_intro_viewed",
  DEVICE_CHECK_VIEWED = "device_check_viewed",
  DEVICE_CHECK_PASSED = "device_check_passed",
  INTERVIEW_JOURNEY_VIEWED = "interview_journey_viewed",
  INTERVIEW_STARTED = "interview_started",
  DEVICE_CHECK_FAILED = "device_check_failed",
  PRE_CHECK_ABANDONED = "pre_check_abandoned",
  TEST_SPEAKER_CLICKED = "test_speaker_clicked",
  CAMERA_DEVICE_CHANGED = "camera_device_changed",
  MIC_DEVICE_CHANGED = "mic_device_changed",

  // ─── Funnel 3: Interview Completion ──────────────────────────────────────
  MODULE_QUESTION_VIEWED = "module_question_viewed",
  RECORDING_STARTED = "recording_started",
  MODULE_SUBMITTED = "module_submitted",
  ROLEPLAY_SESSION_STARTED = "roleplay_session_started",
  ROLEPLAY_SESSION_ENDED = "roleplay_session_ended",
  INTERVIEW_COMPLETED = "interview_completed",
  INTERVIEW_EXITED_EARLY = "interview_exited_early",
  END_CONVERSATION_CONFIRMED = "end_conversation_confirmed",
  MUTE_TOGGLED = "mute_toggled",
  INTERRUPT_CLICKED = "interrupt_clicked",
  SCENARIO_TAB_CLICKED = "scenario_tab_clicked",
  HINTS_TAB_CLICKED = "hints_tab_clicked",
  CAPTION_TAB_CLICKED = "caption_tab_clicked",

  // ─── Funnel 4 & 5 Extras ────────────────────────────────────────────────
  ROLEPLAY_RETRY_CLICKED = "roleplay_retry_clicked",
  NEXT_MODULE_VIEWED = "next_module_viewed",

  // ─── Activity Funnel ──────────────────────────────────────────────────────
  ACTIVITY_MODULE_VIEWED = "activity_module_viewed",
  ACTIVITY_STARTED = "activity_started",
  ACTIVITY_POST_RESULT_ACTION = "activity_post_result_action",

  // ─── Evaluation Funnel ────────────────────────────────────────────────────
  EVALUATION_RECORDING_STARTED = "evaluation_recording_started",
  EVALUATION_SUBMITTED = "evaluation_submitted",
  EVALUATION_POST_RESULT_ACTION = "evaluation_post_result_action",

  // ─── Assessment Funnel ────────────────────────────────────────────────────
  ASSESSMENT_MODULE_VIEWED = "assessment_module_viewed",
  ASSESSMENT_STARTED = "assessment_started",
  ASSESSMENT_SUBMITTED = "assessment_submitted",
  /** Params: action — "next_module" | "previous_module" | "back_to_module" */
  ASSESSMENT_POST_RESULT_ACTION = "assessment_post_result_action",

  // ─── Video Funnel ─────────────────────────────────────────────────────────
  VIDEO_MODULE_VIEWED = "video_module_viewed",
  /** Fired on first play click */
  VIDEO_STARTED = "video_started",
  VIDEO_25_PERCENT = "video_25_percent",
  VIDEO_50_PERCENT = "video_50_percent",
  VIDEO_75_PERCENT = "video_75_percent",
  VIDEO_COMPLETED = "video_completed",
  /** Params: action — "next_module" | "previous_module" */
  VIDEO_POST_RESULT_ACTION = "video_post_result_action",

  // ─── Document Funnel ──────────────────────────────────────────────────────
  DOCUMENT_MODULE_VIEWED = "document_module_viewed",
  /** Fired when user clicks the Open button */
  DOCUMENT_OPENED = "document_opened",
  /** Params: page_number, total_pages */
  DOCUMENT_PAGE_VIEWED = "document_page_viewed",
  /** Fired when user reaches the last page */
  DOCUMENT_COMPLETED = "document_completed",
  /** Fired when the document fails to load. Params: reason */
  DOCUMENT_ERROR = "document_error",
  /** Params: action — "next_module" | "previous_module" */
  DOCUMENT_POST_RESULT_ACTION = "document_post_result_action",

  // ─── AI Coach Events ────────────────────────────────────────────────────────
  AI_COACH_CTA_CLICKED = "ai_coach_cta_clicked",
  AI_COACH_SESSION_STARTED = "ai_coach_session_started",
  AI_COACH_SESSION_COMPLETED = "ai_coach_session_completed",
  AI_COACH_SESSION_ABANDONED = "ai_coach_session_abandoned",
  AI_COACH_ERROR = "ai_coach_error",

  // ─── Normal Coach Events ────────────────────────────────────────────────────
  NORMAL_COACH_STARTED = "normal_coach_started",
  NORMAL_COACH_ERROR = "normal_coach_error",

  // ─── PPT Coach Events ───────────────────────────────────────────────────────
  PPT_COACH_STARTED = "ppt_coach_started",
  PPT_COACH_ERROR = "ppt_coach_error",

  // ─── Global Bot Chat Events ─────────────────────────────────────────────────
  GLOBAL_BOT_CHAT_STARTED = "global_bot_chat_started",
  GLOBAL_BOT_CHAT_ERROR = "global_bot_chat_error",

  // ─── Global Bot Live Agent ──────────────────────────────────────────────────
  GLOBAL_BOT_LIVE_AGENT_STARTED = "global_bot_live_agent_started",
  GLOBAL_BOT_LIVE_AGENT_CLOSED = "global_bot_live_agent_closed",
  GLOBAL_BOT_LIVE_AGENT_ERROR = "global_bot_live_agent_error",

  // ─── Roleplay Briefing Events ──────────────────────────────────────────────
  ROLEPLAY_BRIEFING_CTA_CLICKED = "roleplay_briefing_cta_clicked",
  ROLEPLAY_BRIEFING_STARTED = "roleplay_briefing_started",
  ROLEPLAY_BRIEFING_COMPLETED = "roleplay_briefing_completed",
  ROLEPLAY_BRIEFING_CLOSED = "roleplay_briefing_closed",
  ROLEPLAY_BRIEFING_ERROR = "roleplay_briefing_error",

  // ─── Manager Coaching Events ──────────────────────────────────────────────
  MANAGER_COACHING_CTA_CLICKED = "manager_coaching_cta_clicked",
  MANAGER_COACHING_STARTED = "manager_coaching_started",
  MANAGER_COACHING_COMPLETED = "manager_coaching_completed",
  MANAGER_COACHING_CLOSED = "manager_coaching_closed",
  MANAGER_COACHING_ERROR = "manager_coaching_error",
}

let posthogInitialized = false;

const EXCLUDED_AUTH_PATHS = ["/signin", "/signup", "/forgotpassword"];

function isExcludedAuthPage(): boolean {
  const path = window.location.pathname.toLowerCase();
  return EXCLUDED_AUTH_PATHS.some((excluded) => path.startsWith(excluded));
}

function isInternalUser(customEmail?: string): boolean {
  try {
    const email = customEmail || localStorage.getItem("email");
    return Boolean(email?.toLowerCase().includes("@mple"));
  } catch {
    return false;
  }
}

function isOrgOptedIn(): boolean {
  try {
    const orgStr = localStorage.getItem("org");
    return orgStr ? JSON.parse(orgStr)?.enablePostHog === true : false;
  } catch {
    return false;
  }
}

/**
 * Initialize GA4
 */
export function initPostHog() {
  if (!IS_PROD) return;

  const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  if (!key) return;

  // Do not initialize PostHog on auth pages or for internal users
  // We ALWAYS initialize PostHog now, but we filter events in before_send based on org settings.
  // if (isExcludedAuthPage()) return;
  if (isInternalUser()) return;

  // not return early here so that PostHog can be initialized to track globally allowed events.
  // We will filter out events in before_send if enablePostHog is false.

  posthog.init(key, {
    api_host: "https://p.mple.ai",
    ui_host: "https://us.posthog.com",
    defaults: "2026-05-30",
    autocapture: true,
    capture_dead_clicks: false,
    capture_performance: false,
    capture_pageview: true,
    // posthog.captureLog (logRoleplaySession below): the same consent gate
    // as events.
    logs: {
      serviceName: "mple-fe",
      environment: "prod",
      beforeSend: (record) => (isOrgOptedIn() ? record : null),
    },
    before_send: (event) => {
      if (!event) return null;
      if (event.event === "$dead_click") return null;
      if (event.event === "$web_vitals") return null;
      if (event.event === "$rageclick") return null;

      // block events if org hasn't opted in, UNLESS it's a globally allowed event
      const allowedGlobalEvents = [GA_EVENTS.APP_LOGIN_INITIATED as string];
      const isGlobalEvent = allowedGlobalEvents.includes(event.event);

      // Allow the event through if the organization opted into tracking,
      // OR if it is a globally safe event (like login actions before org is known).
      const shouldAllowEvent = isOrgOptedIn() || isGlobalEvent;
      if (!shouldAllowEvent) {
        return null; // Block and drop the event
      }

      if (event.event === "$exception") {
        const exceptionList = (event.properties?.$exception_list ??
          []) as Exception[];
        for (const exception of exceptionList) {
          const exceptionValue = `${exception.type ?? ""} ${exception.value ?? ""}`;
          // Intentional Next.js route abort while showing leave-session
          // confirmation (assessment / interview). Not a real failure.
          if (
            exceptionValue.includes(
              "Route change aborted. Showing confirmation modal."
            ) ||
            exceptionValue.includes("Cancel rendering route")
          ) {
            return null;
          }
          // Safari/WebKit NullMedia bug: media controls read played/buffered/
          // seekable after teardown and throw "Can't find variable: EmptyRanges".
          // Not our code — drop it so error tracking stays actionable.
          // https://bugs.webkit.org/show_bug.cgi?id=318284
          if (exceptionValue.includes("EmptyRanges")) {
            return null;
          }
          // Stale tab after deploy: Next/Turbopack chunk hash no longer on CDN.
          // Drop only when recovery will/did run (first-shot). Keep repeats after
          // the one-shot guard is already set — those are actionable broken deploys.
          if (
            (exceptionValue.includes("ChunkLoadError") ||
              exceptionValue.includes("Failed to load chunk") ||
              exceptionValue.includes("Loading chunk")) &&
            shouldSuppressChunkLoadException()
          ) {
            return null;
          }

          const frames = exception.stacktrace?.frames ?? [];
          for (const frame of frames) {
            const filename = (frame.filename ?? "").toLowerCase();
            if (
              filename.includes("chrome-extension://") ||
              filename.includes("safari-extension://") ||
              filename.includes("safari-web-extension://") ||
              filename.includes("moz-extension://") ||
              filename.includes("extensions::")
            ) {
              return null;
            }
          }
        }
      }

      return event;
    },
  });
  posthogInitialized = true;
}

/**
 * Enable or disable PostHog capturing
 */
export function togglePostHog(enable: boolean) {
  if (!IS_PROD) return;

  // Disable PostHog on auth pages or for internal users
  if (isExcludedAuthPage()) return;
  if (isInternalUser()) return;

  if (enable) {
    if (posthogInitialized) {
      if (posthog.has_opted_out_capturing()) posthog.opt_in_capturing();
    } else {
      initPostHog();
    }
  } else if (posthogInitialized) {
    posthog.opt_out_capturing();
  }
}

/**
 * Track Page Views
 */
export function trackPageView(path: string) {
  if (!posthogInitialized) return;
  // Do not track auth pages
  const normalizedPath = path.toLowerCase();
  if (
    EXCLUDED_AUTH_PATHS.some((excluded) => normalizedPath.startsWith(excluded))
  )
    return;
}

/**
 * Identify User
 */
export function identifyUser(user: any, organization?: any) {
  const email = user?.email || user?.userId || "";

  if (isInternalUser(email)) {
    // If they are an internal user, make sure we opt out and do not track
    if (posthogInitialized) {
      posthog.opt_out_capturing();
    }
    return;
  }

  if (!posthogInitialized || !user) return;
  // Do not identify on auth pages
  if (isExcludedAuthPage()) return;

  const orgName = organization?.organizationName || organization?.name || "";

  if (email) {
    posthog.identify(email, {
      email: email,
      orgName: orgName,
    });
  }

  if (orgName) {
    posthog.group("company", orgName, {
      name: orgName,
    });
  }
}

/**
 * Allowlist for trackProductEvent() below. Its posthog.capture() call was
 * previously commented out entirely (MPLEAI-2108), so every one of the
 * ~150 GA_EVENTS it's called with (across ~200 call sites) was silently
 * dropped. Re-enabling it is scoped to a named allowlist rather than
 * turned on globally, so modules aren't resurrected before someone
 * decides they're worth the added PostHog volume. Add a module's events
 * here only as part of a deliberate resurrection decision — see the
 * module comments below.
 */
const ENABLED_PRODUCT_EVENTS = new Set<string>([
  // ─── Roleplay Pre-flight (MPLEAI-2108) ────────────────────────────────
  GA_EVENTS.APP_ROLEPLAY_DESCRIPTION_VIEWED,
  // APP_ROLEPLAY_BACK_CLICKED intentionally excluded — it only ever fired
  // alongside APP_ROLEPLAY_EXIT_CLICKED from the same Exit button.
  GA_EVENTS.APP_ROLEPLAY_HINT_TOGGLED,
  GA_EVENTS.APP_ROLEPLAY_EXIT_CLICKED,
  GA_EVENTS.APP_ROLEPLAY_NEXT_CLICKED,
  GA_EVENTS.APP_ROLEPLAY_MIC_SETUP_VIEWED,
  GA_EVENTS.APP_ROLEPLAY_LANGUAGE_SELECTED,
  GA_EVENTS.APP_ROLEPLAY_VIDEO_MODE_SELECTED,
  GA_EVENTS.APP_ROLEPLAY_MIC_SELECTED,
  GA_EVENTS.APP_ROLEPLAY_SPEAKER_SELECTED,
  GA_EVENTS.APP_ROLEPLAY_TEST_SPEAKER_CLICKED,
  GA_EVENTS.APP_ROLEPLAY_PREVIOUS_CLICKED,
  GA_EVENTS.APP_ROLEPLAY_START_CLICKED,

  // ─── Coach Module (MPLEAI-2108) ───────────────────────────────────────
  // APP_COACH_STARTED intentionally excluded — pages/pitch-partner/index.tsx
  // fires it and PPT_COACH_STARTED (already live via trackAiCoachEvent) for
  // the exact same session_ready moment; keeping both would double-count.
  GA_EVENTS.APP_COACH_MODULE_START_CLICKED,
  GA_EVENTS.APP_COACH_MODULE_HISTORY_CLICKED,
  GA_EVENTS.APP_COACH_DESCRIPTION_VIEWED,
  GA_EVENTS.APP_COACH_EXIT_CLICKED,
  GA_EVENTS.APP_COACH_NEXT_CLICKED,
  GA_EVENTS.APP_COACH_PREFLIGHT_VIEWED,
  GA_EVENTS.APP_COACH_LANGUAGE_SELECTED,
  GA_EVENTS.APP_COACH_MODE_SELECTED,
  GA_EVENTS.APP_COACH_PREVIOUS_CLICKED,
  GA_EVENTS.APP_COACH_MUTE_TOGGLED,
  GA_EVENTS.APP_COACH_RESTART_CLICKED,
  GA_EVENTS.APP_COACH_CALL_ENDED,
  GA_EVENTS.APP_COACH_RESULT_VIEWED,
]);

/**
 * Track Custom Product Events
 */
export function trackProductEvent(
  eventName: GA_EVENTS | string,
  params: Record<string, any> = {}
) {
  if (!posthogInitialized) return;
  // Do not track on auth pages
  if (isExcludedAuthPage()) return;
  if (!ENABLED_PRODUCT_EVENTS.has(eventName)) return;
  // Goes through the same posthog.capture() -> before_send -> isOrgOptedIn
  // consent gate as every other capture path in this file (see initPostHog
  // above) — not bypassed or duplicated here.
  posthog.capture(eventName, params);
}

/**
 * Roleplay sessions stream mic audio to the backend with almost no DOM
 * interaction, so PostHog autocapture / replay treat them as idle. Manual
 * capture events keep the product session active and show up on the replay
 * timeline (mic switch, disconnect, etc.).
 *
 * Heartbeat is 60s (not 20s) to limit PostHog volume, and pauses while the
 * tab is hidden so backgrounded sessions do not burn quota.
 */
const ROLEPLAY_SESSION_HEARTBEAT_MS = 60_000;

let roleplaySessionContext: Record<string, any> = {};
let roleplayPeerId: string | undefined;
let roleplayHeartbeatTimer: ReturnType<typeof setInterval> | null = null;
let roleplayHeartbeatActive = false;
let roleplayVisibilityListenerAttached = false;

export function setRoleplaySessionContext(
  ctx: Record<string, any> | null
): void {
  roleplaySessionContext = ctx ? { ...ctx } : {};
}

export function setRoleplayPeerId(pcId: string | null): void {
  roleplayPeerId = pcId ?? undefined;
}

export function hasRoleplaySessionContext(): boolean {
  return Object.keys(roleplaySessionContext).length > 0;
}

export function trackRoleplaySessionEvent(
  eventName: GA_EVENTS | string,
  params: Record<string, any> = {}
) {
  if (!posthogInitialized) return;
  if (isExcludedAuthPage()) return;
  posthog.capture(eventName, {
    ...roleplaySessionContext,
    ...params,
  });
}

export function logRoleplaySession(
  level: "info" | "warn" | "error",
  message: string,
  attributes: Record<string, string | number | boolean | undefined> = {}
) {
  if (!IS_PROD) {
    const line = `[roleplay] ${message}`;
    if (level === "error") console.error(line, attributes);
    else if (level === "warn") console.warn(line, attributes);
    else logger.log(line, attributes);
    return;
  }

  if (!posthogInitialized) return;

  const { trace_id: traceId, ...context } = roleplaySessionContext;
  posthog.captureLog({
    body: message,
    level,
    trace_id: traceId,
    attributes: { ...context, pc_id: roleplayPeerId, ...attributes },
  });
}

function setReplayIdleThresholdInPosthog(ms: number | undefined) {
  if (!posthogInitialized) return;
  posthog.set_config({
    session_recording: {
      ...posthog.config.session_recording,
      session_idle_threshold_ms: ms,
    },
  });
}

export async function getMicSwitchAnalyticsProps(): Promise<
  Record<string, any>
> {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const mics = devices.filter((device) => device.kind === "audioinput");
    // mic_count only — device labels often include personal names (PII).
    return { mic_count: mics.length };
  } catch {
    return {};
  }
}

const clearRoleplayHeartbeatTimer = (): void => {
  if (roleplayHeartbeatTimer) {
    clearInterval(roleplayHeartbeatTimer);
    roleplayHeartbeatTimer = null;
  }
};

const tickRoleplayHeartbeat = (): void => {
  if (typeof document !== "undefined" && document.hidden) return;
  trackRoleplaySessionEvent(GA_EVENTS.APP_ROLEPLAY_SESSION_ACTIVE);
};

const scheduleRoleplayHeartbeat = (): void => {
  clearRoleplayHeartbeatTimer();
  if (!roleplayHeartbeatActive) return;
  if (typeof document !== "undefined" && document.hidden) return;
  roleplayHeartbeatTimer = setInterval(
    tickRoleplayHeartbeat,
    ROLEPLAY_SESSION_HEARTBEAT_MS
  );
};

const handleRoleplayVisibilityChange = (): void => {
  if (!roleplayHeartbeatActive) return;
  if (document.hidden) {
    clearRoleplayHeartbeatTimer();
    return;
  }
  scheduleRoleplayHeartbeat();
};

const ensureRoleplayVisibilityListener = (): void => {
  if (roleplayVisibilityListenerAttached || typeof document === "undefined") {
    return;
  }
  document.addEventListener("visibilitychange", handleRoleplayVisibilityChange);
  roleplayVisibilityListenerAttached = true;
};

const removeRoleplayVisibilityListener = (): void => {
  if (!roleplayVisibilityListenerAttached || typeof document === "undefined") {
    return;
  }
  document.removeEventListener(
    "visibilitychange",
    handleRoleplayVisibilityChange
  );
  roleplayVisibilityListenerAttached = false;
};

export function startRoleplaySessionHeartbeat(
  ctx: Record<string, any> = {}
): void {
  stopRoleplaySessionHeartbeat();
  setRoleplaySessionContext(ctx);
  setReplayIdleThresholdInPosthog(ROLEPLAY_REPLAY_IDLE_MS);
  roleplayHeartbeatActive = true;
  ensureRoleplayVisibilityListener();
  scheduleRoleplayHeartbeat();
}

export function stopRoleplaySessionHeartbeat(): void {
  roleplayHeartbeatActive = false;
  clearRoleplayHeartbeatTimer();
  removeRoleplayVisibilityListener();
  setRoleplaySessionContext(null);
  setReplayIdleThresholdInPosthog(undefined);
}

// AI Coach events are handled through this dedicated function.
export function trackAiCoachEvent(
  eventName: GA_EVENTS | string,
  params: Record<string, any> = {}
) {
  if (!posthogInitialized) return;
  if (isExcludedAuthPage()) return;
  posthog.capture(eventName, params);
}

// Global Bot Live Agent events are tracked using this dedicated function.
export function trackGlobalBotLiveAgentEvent(
  eventName: GA_EVENTS | string,
  params: Record<string, any> = {}
) {
  if (!posthogInitialized) return;
  if (isExcludedAuthPage()) return;
  posthog.capture(eventName, params);
}

export function trackManagerFeedbackEvent(
  eventName: GA_EVENTS | string,
  params: Record<string, any> = {}
) {
  if (!posthogInitialized) return;
  if (isExcludedAuthPage()) return;
  posthog.capture(eventName, params);
}

// Global Bot Chat events are tracked using this dedicated function.
export function trackGlobalBotChatEvent(
  eventName: GA_EVENTS | string,
  params: Record<string, any> = {}
) {
  if (!posthogInitialized) return;
  if (isExcludedAuthPage()) return;
  posthog.capture(eventName, params);
}

export function trackSignIn(
  eventName: GA_EVENTS | string,
  params: Record<string, any> = {}
) {
  posthog.capture(eventName, params);
}
